import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { Select, FormSection } from "../../../../_components/ui";
import TextEditor from "../../../../_components/ui/TextEditor";
import {
  PlanProductPicker,
  LineItemsEditor,
  DiscountEditor,
  TaxEditor,
  PriceSummary,
} from "../../../../_components/billing";
import { useGetSubscriptionFormCompanyMutation } from "../../../../_features/upsellDownsell/api/upsellApiSlice";
import { billingCycles } from "../../../../_config/billingOptions";

const BILLING_CYCLE_OPTIONS = (billingCycles || []).map((cycle) =>
  typeof cycle === "object" ? cycle : { label: cycle, value: cycle }
);

/**
 * Step 2 - pick the subscription this upsell/downsell modifies, its billing
 * cycle, and edit the resulting billing items via the shared
 * `_components/billing` engine (PlanProductPicker + editors + summary) -
 * same composition `SubscriptionFormBody`/`ProposalPackageDetailsStep` use.
 * Was the old-tree `BillingItemsTable` until `usePricingState`/
 * `PlanProductPicker` gained hydration support (`fromBillingSubmitPayload`)
 * - see useUpsellWizard's docstring. `catalogs` (plans/products/bundles) is
 * fetched by the host, mirroring `SubscriptionFormModal`.
 *
 * Fetches the company's subscriptions itself (mirrors the old
 * UpsellConfigure exactly) since the list depends on step 1's company
 * selection, which isn't known until the wizard is already open.
 */
const UpsellPackageDetailsStep = ({ control, watch, wizard, pricing, catalogs, isPreview }) => {
  const company = watch("company");
  const subscription = watch("subscription");
  const billingCycle = watch("billingCycle");

  const [subscriptions, setSubscriptions] = useState([]);
  const [getSubscriptionsApiCall, { isLoading: isLoadingSubscriptions }] =
    useGetSubscriptionFormCompanyMutation();

  useEffect(() => {
    if (!company?._id) {
      setSubscriptions([]);
      return;
    }
    let isCancelled = false;
    getSubscriptionsApiCall({ company: company._id })
      .unwrap()
      .then((data) => {
        if (!isCancelled) setSubscriptions(data || []);
      })
      .catch(() => {
        if (!isCancelled) setSubscriptions([]);
      });
    return () => {
      isCancelled = true;
    };
  }, [company?._id, getSubscriptionsApiCall]);

  // Resolve a seeded subscriptionId (opened from SubscriptionUpsells) into
  // the real subscription doc once the list arrives, same as picking it
  // from the dropdown - see useUpsellWizard's seedSubscriptionId docstring.
  useEffect(() => {
    if (!wizard.seedSubscriptionId || subscription || subscriptions.length === 0) return;
    const match = subscriptions.find(
      (item) => item._id === wizard.seedSubscriptionId || item.subscriptionId === wizard.seedSubscriptionId
    );
    if (match) wizard.selectSubscription(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard.seedSubscriptionId, subscriptions, subscription]);

  const market = subscription?.marketId || subscription?.market;
  const currencySign = market?.currency?.[0]?.sign || "";

  return (
    <div className="upsell-package-details-step">
      <FormSection title="Basic details">
        <Select
          label="Subscription"
          options={subscriptions.map((item) => ({
            label: item.subscriptionName,
            value: item._id || item.subscriptionId,
          }))}
          value={subscription?._id || subscription?.subscriptionId}
          onChange={(value) => {
            const selected = subscriptions.find(
              (item) => (item._id || item.subscriptionId) === value
            );
            if (selected) wizard.selectSubscription(selected);
          }}
          disabled={isPreview || !company?._id || isLoadingSubscriptions}
          placeholder={isLoadingSubscriptions ? "Loading subscriptions..." : "Select"}
        />

        <Controller
          name="billingCycle"
          control={control}
          rules={{ required: "Billing cycle is required." }}
          render={({ field, fieldState }) => (
            <Select
              id="billingCycle"
              label="Billing cycle"
              options={BILLING_CYCLE_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
              disabled={isPreview}
            />
          )}
        />

        <div className="upsell-package-details-step__price">
          <h4>Price</h4>
          <span>
            {currencySign} {wizard.totalAmountValue}
          </span>
        </div>
      </FormSection>

      <FormSection>
        <PlanProductPicker
          entities={catalogs.plans}
          bundlesCatalog={catalogs.bundles}
          items={pricing.plans}
          setRawItems={pricing.setPlans}
          currencySign={currencySign}
          selectedBillingCycle={billingCycle}
          nameField="planName"
          title="Plans"
          entityLabel="Plan"
        />
      </FormSection>

      <FormSection>
        <PlanProductPicker
          entities={catalogs.products}
          items={pricing.products}
          setRawItems={pricing.setProducts}
          currencySign={currencySign}
          selectedBillingCycle={billingCycle}
          nameField="productName"
          title="Products"
          entityLabel="Product"
        />
      </FormSection>

      <FormSection>
        <LineItemsEditor items={pricing.lineItems} setRawItems={pricing.setLineItems} currencySign={currencySign} />

        <DiscountEditor discount={pricing.discount} setDiscount={pricing.setDiscount} currencySign={currencySign} />

        <TaxEditor taxes={pricing.taxes} setRawTaxes={pricing.setTaxes} currencySign={currencySign} />

        <PriceSummary totals={pricing.totals} currencySign={currencySign} />
      </FormSection>

      <FormSection title="Invoice terms">
        <Controller
          name="invoiceTerms"
          control={control}
          render={({ field }) => (
            <TextEditor
              textEditorValue={field.value}
              setTextEditorValue={field.onChange}
              placeholder="Invoice terms"
              disabled={isPreview}
            />
          )}
        />
      </FormSection>
    </div>
  );
};

export default UpsellPackageDetailsStep;
